import React, { useEffect, useState, useContext, useRef, useCallback, useMemo } from "react";
import { PlatformAuthContext } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { PageRenderer } from "../pageBuilder/PageRenderer";
import { PageBuilder as PageBuilderComponent } from "../pageBuilder/PageBuilder";
import { IComponentNode } from "../../types/pageBuilder.types";
import { FaEdit } from "react-icons/fa";
import { StoreProvider } from "context/StoreContext";

interface PlatformZoneProps {
 zoneName: string;
 pageSlug: string;
 fallback?: React.ReactNode;
}

// Module-level cache that persists across component instances
const persistentCache = new Map<string, IComponentNode | null>();

// Cache for rendered JSX elements to prevent flickering
// Key: cacheKey, Value: { contentHash, element }
const renderedElementCache = new Map<string, { contentHash: string; element: React.ReactElement }>();

// Memoized PageRenderer component to prevent unnecessary re-renders
const CachedPageRenderer = React.memo<{ layout: IComponentNode; isPreview: boolean }>(
 ({ layout, isPreview }) => {
  return <PageRenderer layout={layout} isPreview={isPreview} />;
 },
 (prevProps, nextProps) => {
  // Only re-render if layout content actually changed
  return JSON.stringify(prevProps.layout) === JSON.stringify(nextProps.layout);
 }
);

CachedPageRenderer.displayName = "CachedPageRenderer";

const getCacheKey = (pageSlug: string, zoneName: string): string => {
 return `${pageSlug}:${zoneName}`;
};

// Deep comparison function to check if zone content has changed
const deepEqual = (a: IComponentNode | null, b: IComponentNode | null): boolean => {
 if (a === b) return true;
 if (a === null || b === null) return false;
 
 return JSON.stringify(a) === JSON.stringify(b);
};

// Helper function to check if a zone is empty
const isZoneEmpty = (zone: IComponentNode | null): boolean => {
 if (!zone) return true;
 
 if (zone.type === "container" || zone.type === "grid") {
  // Check if container has children with content
  if (zone.children && zone.children.length > 0) {
   return zone.children.every((child) => isZoneEmpty(child));
  }
  
  // If no children, check if container has any visual styling that makes it non-empty
  // A container with background color, padding, or other visual properties should be considered non-empty
  const hasVisualStyles = 
   (zone.styles?.default?.backgroundColor) ||
   (zone.styles?.default?.background) ||
   (zone.styles?.default?.backgroundImage) ||
   (zone.styles?.default?.border) ||
   (zone.styles?.default?.borderColor) ||
   (zone.styles?.default?.borderWidth) ||
   (zone.props?.className && zone.props.className.includes("bg-")) ||
   (zone.props?.style && typeof zone.props.style === "object");
  
  // If container has visual styles but no children, it's not empty (it has visual presence)
  if (hasVisualStyles) {
   return false;
  }
  
  // Otherwise, empty container is considered empty
  return true;
 }
 
 if (zone.type === "text" || zone.type === "heading") {
  return !zone.props?.text || zone.props.text.trim() === "";
 }
 
 if (zone.type === "image") {
  return !zone.props?.src;
 }
 
 return false;
};

export const PlatformZone: React.FC<PlatformZoneProps> = ({ zoneName, pageSlug, fallback }) => {
 // Safely access context - returns undefined if provider is not available
 const authContext = useContext(PlatformAuthContext);
 const user = authContext?.user || null;
 const isPlatformAdmin = user?.role === "super_admin";
 
 // Initialize state from cache synchronously to prevent flickering
 const cacheKey = getCacheKey(pageSlug, zoneName);
 const cachedContent = persistentCache.get(cacheKey);
 const hasCachedContent = cachedContent !== undefined;
 
 // Local cache state - initialize from cache if available
 const [zoneContent, setZoneContent] = useState<IComponentNode | null>(
  hasCachedContent ? cachedContent : null
 );
 const [loading, setLoading] = useState(!hasCachedContent);
 const [isHovered, setIsHovered] = useState(false);
 const [isEditing, setIsEditing] = useState(false);
 const [initialLoadComplete, setInitialLoadComplete] = useState(hasCachedContent);
 
 // Refs for internal caching and state tracking
 const cacheKeyRef = useRef<string>(cacheKey);
 const localCacheRef = useRef<IComponentNode | null>(hasCachedContent ? cachedContent : null);
 const isFetchingRef = useRef<boolean>(false);
 const renderedElementRef = useRef<React.ReactElement | null>(null);

 const isEmpty = isZoneEmpty(zoneContent);

 // Generate content hash for cache key
 const contentHash = useMemo(() => {
  if (!zoneContent) return "null";
  return JSON.stringify(zoneContent);
 }, [zoneContent]);

 // Memoized rendered element - cached to prevent flickering
 const renderedElement = useMemo(() => {
  // Only return null if zoneContent is actually null or truly empty
  // Don't hide content that exists just because isEmpty might be incorrectly calculated
  if (!zoneContent) {
   renderedElementRef.current = null;
   return null;
  }
  
  // If zone has content, check if it's actually empty
  if (isEmpty) {
   renderedElementRef.current = null;
   return null;
  }

  // Check if we have a cached rendered element with matching content
  const cached = renderedElementCache.get(cacheKeyRef.current);
  if (cached && cached.contentHash === contentHash) {
   renderedElementRef.current = cached.element;
   return cached.element;
  }

  // Create new rendered element and cache it
  const element = <CachedPageRenderer layout={zoneContent} isPreview />;
  renderedElementCache.set(cacheKeyRef.current, {
   contentHash,
   element,
  });
  renderedElementRef.current = element;
  return element;
 }, [contentHash, zoneContent, isEmpty]);

 // Memoized load function to prevent unnecessary re-renders
 const loadZoneContent = useCallback(async (isInitialLoad: boolean) => {
  // Prevent concurrent fetches
  if (isFetchingRef.current) {
   return;
  }

  try {
   isFetchingRef.current = true;
   
   if (isInitialLoad) {
    setLoading(true);
   }
   
   // First, ensure the page exists with zones
   await platformApiService.ensurePlatformPageZones(pageSlug, [zoneName]);
   
   // Then get the zone content
   const response = await platformApiService.getPlatformZone(pageSlug, zoneName);
   const content = response.data?.zone || null;
   
   // Debug: Log zone content to help diagnose issues
   if (process.env.NODE_ENV === "development") {
    console.log(`[PlatformZone] Loaded zone "${zoneName}" for page "${pageSlug}":`, {
     hasContent: !!content,
     isEmpty: content ? isZoneEmpty(content) : true,
     contentType: content?.type,
     hasChildren: content?.children?.length > 0,
    });
   }
   
   // Update persistent cache
   persistentCache.set(cacheKeyRef.current, content);
   
   if (isInitialLoad) {
    // Initial load - always update state and cache
    setZoneContent(content);
    localCacheRef.current = content;
    setInitialLoadComplete(true);
    setLoading(false);
    // Rendered element will be updated via useMemo when content changes
   } else {
    // Background update - only update if data is different
    if (!deepEqual(localCacheRef.current, content)) {
     setZoneContent(content);
     localCacheRef.current = content;
     // Rendered element will be updated via useMemo when content changes
    }
   }
  } catch (error: any) {
   // If page doesn't exist, create it
   if (error.response?.status === 404) {
    try {
     await platformApiService.ensurePlatformPageZones(pageSlug, [zoneName]);
     const emptyContent = null;
     
     // Update persistent cache
     persistentCache.set(cacheKeyRef.current, emptyContent);
     
     if (isInitialLoad) {
      setZoneContent(emptyContent);
      localCacheRef.current = emptyContent;
      setInitialLoadComplete(true);
      setLoading(false);
      // Rendered element will be updated via useMemo when content changes
     } else {
      // Background update - only update if data is different
      if (!deepEqual(localCacheRef.current, emptyContent)) {
       setZoneContent(emptyContent);
       localCacheRef.current = emptyContent;
       // Rendered element will be updated via useMemo when content changes
      }
     }
    } catch (createError) {
     console.error("Error creating platform page:", createError);
     if (isInitialLoad) {
      setZoneContent(null);
      setLoading(false);
     }
    }
   } else {
    console.error("Error loading zone content:", error);
    if (isInitialLoad) {
     setZoneContent(null);
     setLoading(false);
    }
   }
  } finally {
   isFetchingRef.current = false;
  }
 }, [pageSlug, zoneName]);

 // Initial load effect - only runs when pageSlug or zoneName changes
 useEffect(() => {
  const newCacheKey = getCacheKey(pageSlug, zoneName);
  
  // Only proceed if cache key actually changed
  if (cacheKeyRef.current === newCacheKey && initialLoadComplete) {
   return;
  }
  
  cacheKeyRef.current = newCacheKey;
  
  // Check persistent cache first
  const cachedContent = persistentCache.get(newCacheKey);
  
  if (cachedContent !== undefined) {
   // Cache exists - ensure state is set (may already be set from useState init)
   if (zoneContent !== cachedContent) {
    setZoneContent(cachedContent);
    localCacheRef.current = cachedContent;
   }
   setInitialLoadComplete(true);
   setLoading(false);
   
   // Fetch updates in the background
   loadZoneContent(false);
  } else {
   // No cache - reset state and show loading
   setInitialLoadComplete(false);
   localCacheRef.current = null;
   setZoneContent(null);
   setLoading(true);
   
   // Initial load - show loading until data is received
   loadZoneContent(true);
  }
 }, [pageSlug, zoneName, loadZoneContent]);

 // Background update effect - fetches updates after initial load
 useEffect(() => {
  // Only start background updates after initial load is complete
  if (!initialLoadComplete || isFetchingRef.current) {
   return;
  }

  // Fetch updates in the background
  const fetchInterval = setInterval(() => {
   if (!isFetchingRef.current) {
    loadZoneContent(false);
   }
  }, 30000); // Fetch updates every 30 seconds

  return () => clearInterval(fetchInterval);
 }, [pageSlug, zoneName, initialLoadComplete, loadZoneContent]);

 const handleEditClick = () => {
  setIsEditing(true);
 };

 const handleSaveZone = async (layout: IComponentNode) => {
  try {
   setLoading(true);
   await platformApiService.updatePlatformZone(pageSlug, zoneName, layout);
   // Small delay to ensure database write is complete
   await new Promise(resolve => setTimeout(resolve, 200));
   // Update cache and state immediately with saved content
   persistentCache.set(cacheKeyRef.current, layout);
   setZoneContent(layout);
   localCacheRef.current = layout;
   // Clear rendered element cache to force re-render with new content
   renderedElementCache.delete(cacheKeyRef.current);
   setIsEditing(false);
   setLoading(false);
   // Force a refresh of the zone content to ensure it's loaded from the database
   // Clear cache first to force fresh fetch
   persistentCache.delete(cacheKeyRef.current);
   await loadZoneContent(false);
  } catch (err: any) {
   console.error("Error saving zone:", err);
   alert(err.response?.data?.error || "Failed to save zone");
   setLoading(false);
  }
 };

 const handleCancelEdit = () => {
  setIsEditing(false);
 };

 // Show loading only on initial load when we don't have cached data
 if (loading && !initialLoadComplete) {
  return (
   <div className="relative min-h-[120px] py-8">
    <div className="shimmer h-32 w-full"></div>
   </div>
  );
 }

 // Zone editor modal - allow editing even if zone is empty (to create new zone)
 if (isEditing && isPlatformAdmin) {
  return (
   <StoreProvider>
    <div className="fixed inset-0 flex flex-col bg-gray-100 z-[9999]">
     <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
      <div>
       <h2 className="text-lg font-semibold text-gray-900">
        Edit Zone: {zoneName}
       </h2>
       <p className="text-sm text-gray-500 mt-1">
        Page: <strong>{pageSlug}</strong>
       </p>
      </div>
      <button
       onClick={handleCancelEdit}
       className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
      >
       Cancel
      </button>
     </div>
     <div className="flex-1 overflow-hidden">
      <PageBuilderComponent
       initialLayout={zoneContent || {
        id: `zone-${zoneName}`,
        type: "container",
        props: {},
        styles: { default: {} },
        children: [],
       }}
       onCancel={handleCancelEdit}
       onSave={handleSaveZone}
       zoneName={zoneName}
       pageName={pageSlug}
       pageType="platform"
      />
     </div>
    </div>
   </StoreProvider>
  );
 }

 // Show fallback if zone is empty, but still allow editing
 // For admins: show placeholder with edit option
 // For public users: show nothing (completely hidden)
 // Only check isEmpty if zoneContent exists (not null) - don't hide while loading
 if (zoneContent && isEmpty) {
  // Public users should not see empty zones at all
  if (!isPlatformAdmin) {
   return null;
  }
  
  // Admins see placeholder with edit option
  return (
   <div
    className="relative group cursor-pointer min-h-[120px] py-8"
    onMouseEnter={() => setIsHovered(true)}
    onMouseLeave={() => setIsHovered(false)}
    onClick={handleEditClick}
   >
    <div className="relative border-2 border-dashed border-gray-300 bg-gray-50/50 transition-all hover:border-blue-400 hover:bg-blue-50/30">
     <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
      <div className="text-center text-gray-400 text-sm">
       <p className="font-medium mb-1">Empty {zoneName} zone</p>
       <p className="text-xs">Click anywhere to edit</p>
      </div>
     </div>
     <div className="opacity-30">
      {fallback || (
       <div className="p-8 text-center text-gray-400">
        Empty zone - Click to add content
       </div>
      )}
     </div>
    </div>
   </div>
  );
 }

 // If zoneContent is null and we're not loading, it means the zone doesn't exist
 // For public users, don't show anything
 if (!zoneContent && !loading && initialLoadComplete) {
  if (!isPlatformAdmin) {
   return null;
  }
  // Admins can still see and edit non-existent zones
  return (
   <div
    className="relative group cursor-pointer min-h-[120px] py-8"
    onMouseEnter={() => setIsHovered(true)}
    onMouseLeave={() => setIsHovered(false)}
    onClick={handleEditClick}
   >
    <div className="relative border-2 border-dashed border-gray-300 bg-gray-50/50 transition-all hover:border-blue-400 hover:bg-blue-50/30">
     <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
      <div className="text-center text-gray-400 text-sm">
       <p className="font-medium mb-1">Zone not found: {zoneName}</p>
       <p className="text-xs">Click anywhere to create</p>
      </div>
     </div>
    </div>
   </div>
  );
 }

 const handleDoubleClick = () => {
  if (isPlatformAdmin) {
   handleEditClick();
  }
 };

 return (
  <div
   className={`relative group ${isPlatformAdmin ? "cursor-pointer" : ""} ${isEmpty ? "min-h-[120px] py-8" : ""}`}
   onMouseEnter={() => isPlatformAdmin && setIsHovered(true)}
   onMouseLeave={() => setIsHovered(false)}
   onClick={isPlatformAdmin && isEmpty ? handleEditClick : undefined}
   onDoubleClick={handleDoubleClick}
  >
   {isPlatformAdmin && !isEditing && !isEmpty && isHovered && (
    <button
     onClick={(e) => {
      e.stopPropagation();
      handleEditClick();
     }}
     className="absolute top-2 right-2 z-50 bg-blue-600 text-white p-2  hover:bg-blue-700 transition-colors flex items-center gap-2"
     title={`Edit ${zoneName} zone`}
    >
     <FaEdit className="text-sm" />
     <span className="text-xs font-medium">Edit Zone</span>
    </button>
   )}
   <div
    className={`relative ${
     isPlatformAdmin
      ? `border-2 border-dashed transition-all ${
        isEmpty
         ? "border-gray-300 bg-gray-50/50 hover:border-blue-400 hover:bg-blue-50/30 min-h-[100px]"
         : "border-transparent group-hover:border-blue-300"
       }`
      : ""
    }`}
   >
    <div className={isEmpty ? "opacity-30" : ""}>
     {renderedElement || (zoneContent ? <CachedPageRenderer layout={zoneContent} isPreview={false} /> : null)}
    </div>
   </div>
  </div>
 );
};

